// @ts-nocheck
import React, { useCallback } from 'react';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import PropTypes from 'prop-types';
import DialogTransition from '../../../components/Common/Transition/DialogTransition';
import DocumentEditor from './DocumentEditor/DocumentEditor';
import swalToast from '../../../components/Common/swalToast';
import { downloadBlobResponse } from '../../../utils/Utils';
import { useDownloadDocumentMutation } from '../api/documentInventoryQueries';
import PdfIframe from './PdfIframe';

/**
 * Downloads a document blob response with formatted file name and extension.
 * @param {Blob|Object} downloadData - File data blob
 * @param {string} documentName - Name of the document
 * @param {string} fileType - Extension or document type
 */
const downloadTemplate = (downloadData, documentName, fileType) => {
  try {
    const normalizedFileType = fileType === 'onedrive_docx' ? 'docx' : fileType;
    const fileName =
      normalizedFileType &&
      !documentName
        ?.toLowerCase()
        .endsWith(`.${normalizedFileType.toLowerCase()}`)
        ? `${documentName}.${normalizedFileType}`
        : documentName;
    downloadBlobResponse({ data: downloadData }, fileName);
  } catch (e) {
    swalToast({
      icon: 'error',
      title: `Error occurred at time of downloading file. ${e}`,
    });
  }
};

const DocumentPreviewModal = ({
  open,
  handleClose,
  saveAsNewVersion,
  updateDocument,
  getVersionDetails,
  getDocumentMarkdown,
  selectedDocumentForEdit,
  setSelectedDocumentForEdit,
}) => {
  const { mutateAsync: downloadDocument } = useDownloadDocumentMutation();
  const {
    file_type,
    is_editable,
    document_name,
    document_id,
    entity_id,
    entity_type,
  } = selectedDocumentForEdit || {};
  const isPDFFile = file_type === 'pdf';
  // Fallback to handle old files as they don't have is_editable attr.
  const isUnsupportedFileFallBack = !['pdf', 'txt', 'md'].includes(file_type);
  const isUnsupportedFile =
    is_editable === undefined
      ? isUnsupportedFileFallBack
      : isUnsupportedFileFallBack || !is_editable;

  /**
   * Closes dialogue, resets the associated Formik form, and triggers the closure of the main modal.
   * @returns {void} - No return value.
   */
  const handleDialogueClose = useCallback(() => {
    handleClose();
    setSelectedDocumentForEdit({});
  }, [handleClose, setSelectedDocumentForEdit]);

  const fetchDocumentsList = useCallback(() => {}, []);

  const handleDownload = useCallback(() => {
    downloadDocument({
      params: {
        document_id,
        entity_id,
        entity_type,
        export_type: file_type,
        commit_id:
          selectedDocumentForEdit?.versions?.length > 0
            ? `${selectedDocumentForEdit?.versions[selectedDocumentForEdit.versions.length - 1]?.version}`
            : 1,
      },
      config: {
        responseType: file_type === 'onedrive_docx' ? 'json' : 'blob',
        loader: false,
      },
    }).then((response) => {
      if (response) {
        if (file_type === 'onedrive_docx') {
          window.open(
            response?.data?.Data || response?.data?.path || response?.path,
            '_blank'
          );
        } else {
          downloadTemplate(response, document_name, file_type);
        }
      }
    });
  }, [
    downloadDocument,
    selectedDocumentForEdit,
    document_id,
    entity_id,
    entity_type,
    file_type,
    document_name,
  ]);

  const renderUnsupportedFileWarning = () => {
    if (isUnsupportedFile) {
      return (
        <Stack
          sx={{
            padding: '32px 16px',
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
            }}
          >
            File cannot be previewed here. Please download it to view or edit.
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  const displayFileType = file_type === 'onedrive_docx' ? 'docx' : file_type;
  const fullDocumentTitle =
    displayFileType &&
    !document_name?.toLowerCase().endsWith(`.${displayFileType.toLowerCase()}`)
      ? `${document_name}.${displayFileType}`
      : document_name;

  return (
    <Box>
      <Dialog
        maxWidth={isUnsupportedFile ? 'sm' : 'xl'}
        fullWidth={!isUnsupportedFile}
        open={open}
        onClose={handleDialogueClose}
        slots={{
          transition: DialogTransition,
        }}
        PaperProps={{
          sx: {
            maxHeight: '70vh',
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle title={fullDocumentTitle}>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '85%',
            }}
          >
            Document Preview — {fullDocumentTitle}
          </Typography>

          <IconButton
            color="primary"
            aria-label="Close dialog"
            onClick={handleDialogueClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 12,
              color: 'text.secondary',
            }}
          >
            <CloseIcon width={18} height={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* Inner box container */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              p: 2.5,
              minHeight: 340,
              maxHeight: '52vh',
              overflowY: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {renderUnsupportedFileWarning()}
            {!isUnsupportedFile && !isPDFFile && (
              <Grid container rowSpacing={5} size={12}>
                <Grid size={12}>
                  <DocumentEditor
                    mode="view"
                    docDetails={selectedDocumentForEdit}
                    getDocumentMarkdown={getDocumentMarkdown}
                    getVersionDetails={getVersionDetails}
                    updateDocument={updateDocument}
                    // isUserAuthorized={isUserAuthorized}
                    saveAsNewVersion={saveAsNewVersion}
                    fetchDocumentsList={fetchDocumentsList}
                  />
                </Grid>
              </Grid>
            )}
            {!isUnsupportedFile && isPDFFile && (
              <Grid container size={12}>
                <Grid size={12}>
                  <PdfIframe
                    docDetails={selectedDocumentForEdit}
                    downloadDocument={getDocumentMarkdown}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleDialogueClose}
            sx={{ minWidth: 90 }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleDownload}
            sx={{ minWidth: 100 }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
DocumentPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  saveAsNewVersion: PropTypes.func.isRequired,
  updateDocument: PropTypes.func.isRequired,
  getVersionDetails: PropTypes.func.isRequired,
  getDocumentMarkdown: PropTypes.func.isRequired,
  setSelectedDocumentForEdit: PropTypes.func.isRequired,
  selectedDocumentForEdit: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default DocumentPreviewModal;
